/**
 * File Analysis Service
 * Extracts text from uploaded documents and uses AI to generate real summaries.
 * Falls back to filename-based summaries if AI is unavailable.
 */

import { extractTextFromFile, truncateForAI } from '../utils/documentTextExtractor';
import { summarizeDocument } from './aiService';

/**
 * Analyze a document: extract text, generate an AI summary, and extract keywords.
 * Falls back to a filename-based summary if AI fails.
 * @param {File} file - The File object to analyze
 * @returns {Promise<{summary: string, confidence: number, keywords: string[], extractedText: string}>}
 */
export const analyzeDocument = async (file) => {
  let extractedText = '';

  // Step 1: Extract text from the file
  try {
    extractedText = await extractTextFromFile(file);
  } catch (err) {
    console.warn('[fileAnalysis] Text extraction failed:', err.message);
  }

  const truncated = truncateForAI(extractedText, 3000);

  // Step 2: Try AI-powered summary
  if (truncated && truncated.length >= 20) {
    try {
      const aiSummary = await summarizeDocument(truncated, file.name);
      if (aiSummary && aiSummary.length > 10) {
        return {
          summary: aiSummary,
          confidence: 0.9,
          keywords: extractKeywords(extractedText || file.name),
          suggestedCategories: suggestCategories(file.name),
          extractedText
        };
      }
    } catch (err) {
      console.warn('[fileAnalysis] AI summary failed, falling back to filename-based:', err.message);
    }
  }

  // Step 3: Fallback to filename-based summary
  return {
    summary: generateFallbackSummary(file.name),
    confidence: 0.3,
    keywords: extractKeywords(file.name),
    suggestedCategories: suggestCategories(file.name),
    extractedText
  };
};

// ─────────────── Fallback summary (original logic) ───────────────

const generateFallbackSummary = (fileName) => {
  const lowerName = fileName.toLowerCase();

  if (lowerName.includes('resume') || lowerName.includes('cv')) {
    return 'This appears to be a resume or CV document that highlights professional background, skills, and experience. Review and customize this summary to accurately reflect the document\'s specific content.';
  }
  if (lowerName.includes('report') || lowerName.includes('analysis')) {
    return 'This document appears to be a report or analysis showcasing analytical and reporting capabilities. Customize this summary to describe the specific content.';
  }
  if (lowerName.includes('proposal') || lowerName.includes('plan')) {
    return 'This appears to be a proposal or planning document demonstrating strategic thinking and project management skills. Edit to reflect the actual content.';
  }
  if (lowerName.includes('certificate') || lowerName.includes('cert')) {
    return 'This appears to be a certificate or credential document. Edit the summary to describe the certification and its relevance.';
  }
  if (lowerName.includes('award') || lowerName.includes('trophy')) {
    return 'This appears to be an award or recognition document. Edit the summary to describe the achievement.';
  }
  if (lowerName.includes('training') || lowerName.includes('workshop')) {
    return 'This appears to be training or workshop material showcasing instructional design and knowledge transfer capabilities.';
  }
  if (lowerName.includes('presentation') || lowerName.includes('ppt')) {
    return 'This presentation demonstrates skills in visual communication, design, and presenting complex ideas. Edit this summary to describe the specific topic.';
  }
  if (lowerName.includes('spreadsheet') || lowerName.includes('xls') || lowerName.includes('excel')) {
    return 'This spreadsheet demonstrates proficiency with data management, analysis, and presentation. Customize to describe the specific data or insights.';
  }

  return 'This document contains professional content related to your portfolio. Review and edit this summary to better describe the contents and your contributions.';
};

// ─────────────── Keyword & Category Helpers ───────────────

const extractKeywords = (text) => {
  const commonWords = ['the', 'a', 'an', 'and', 'or', 'but', 'in', 'on', 'at', 'to', 'for', 'of', 'with', 'by', 'from', 'this', 'that', 'is', 'was', 'are', 'were', 'be', 'been', 'has', 'have', 'had', 'do', 'does', 'did', 'will', 'would', 'could', 'should'];
  const words = text
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, ' ')
    .split(/\s+/)
    .filter((word) => word.length > 2 && !commonWords.includes(word));

  // Count frequency
  const freq = {};
  words.forEach((w) => { freq[w] = (freq[w] || 0) + 1; });

  return Object.entries(freq)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 10)
    .map(([word]) => word);
};

const suggestCategories = (fileName) => {
  const categories = [];
  const lowerName = fileName.toLowerCase();

  if (lowerName.includes('resume') || lowerName.includes('cv')) categories.push('resume');
  if (lowerName.includes('project')) categories.push('projects');
  if (lowerName.includes('report') || lowerName.includes('analysis')) categories.push('publications');
  if (lowerName.includes('award') || lowerName.includes('certificate')) categories.push('awards');
  if (lowerName.includes('training') || lowerName.includes('workshop')) categories.push('training');

  return categories;
};

export default {
  analyzeDocument
};
